import { useState, useEffect } from "react";
import { Copy, Check, Globe, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const LANGS = ["English", "Spanish", "Mandarin", "French", "Portuguese", "Korean"];

function printContent(title, text) {
  const w = window.open("", "_blank", "width=800,height=900");
  if (!w) { toast.error("Allow pop-ups to print."); return; }
  const safe = (text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
  w.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
    <style>
      body{font-family:Georgia,serif;color:#111;max-width:680px;margin:48px auto;padding:0 32px;line-height:1.6;}
      h1{font-size:24px;border-bottom:2px solid #D4AF37;padding-bottom:10px;margin-bottom:20px;}
      pre{white-space:pre-wrap;font-family:Georgia,serif;font-size:15px;}
      .brand{font-size:13px;color:#888;margin-top:40px;border-top:1px solid #ddd;padding-top:12px;}
    </style></head><body>
    <h1>${title}</h1><pre>${safe}</pre>
    <div class="brand">Generated with PenhausAI</div>
    <script>window.onload=function(){window.print();}</script>
    </body></html>`);
  w.document.close();
}

export function OutputCard({ title, icon: Icon, children, copyText, testid, printable }) {
  const [copied, setCopied] = useState(false);
  const [lang, setLang] = useState("English");
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [cache, setCache] = useState({});

  useEffect(() => {
    setLang("English");
    setTranslated(null);
    setCache({});
  }, [copyText]);

  const displayText = lang === "English" ? copyText : (translated ?? copyText);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(displayText || "");
      setCopied(true);
      toast.success(`${title} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const changeLang = async (next) => {
    setLang(next);
    if (next === "English") { setTranslated(null); return; }
    if (cache[next]) { setTranslated(cache[next]); return; }
    setTranslating(true);
    try {
      const { data } = await api.post("/translate", { text: copyText, language: next });
      setCache((c) => ({ ...c, [next]: data.text }));
      setTranslated(data.text);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLang("English");
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-sm transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50/50 gap-3">
        <div className="flex items-center gap-2 text-slate-700 min-w-0">
          {Icon && <Icon className="h-4 w-4 shrink-0" />}
          <span className="font-semibold text-sm truncate">{title}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-1 text-slate-500">
            <Globe className="h-3.5 w-3.5" />
            <select value={lang} onChange={(e) => changeLang(e.target.value)} data-testid={`lang-${testid}`}
              className="text-xs bg-transparent border-0 focus:ring-0 cursor-pointer text-slate-600 hover:text-slate-900 outline-none">
              {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          {printable && (
            <button onClick={() => printContent(title, displayText)} data-testid={`print-${testid}`} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 transition-colors">
              <Printer className="h-3.5 w-3.5" /> Print
            </button>
          )}
          {copyText != null && (
            <button onClick={copy} data-testid={`copy-${testid}`} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 transition-colors">
              {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          )}
        </div>
      </div>
      <div className="p-5 text-slate-700 text-[15px] leading-relaxed whitespace-pre-wrap min-h-[60px]">
        {translating ? <span className="text-slate-400 text-sm">Translating to {lang}…</span> : (lang === "English" ? children : displayText)}
      </div>
    </div>
  );
}

export function CompliancePanel({ compliance }) {
  if (!compliance) return null;
  const flags = compliance.flags || [];
  const compliant = compliance.compliant && flags.length === 0;
  return (
    <div className={`rounded-lg border p-5 ${compliant ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`} data-testid="compliance-panel">
      <div className="flex items-center gap-2">
        <span className={`text-sm font-semibold ${compliant ? "text-green-700" : "text-red-700"}`} data-testid="compliance-status">
          {compliant ? "✓ Fair Housing Compliant" : `⚠ ${flags.length} potential issue${flags.length > 1 ? "s" : ""} found`}
        </span>
      </div>
      {compliance.summary && <p className="text-sm text-slate-600 mt-2">{compliance.summary}</p>}
      {flags.length > 0 && (
        <ul className="mt-4 space-y-3">
          {flags.map((f, i) => (
            <li key={i} className="bg-white border border-red-100 rounded-md p-3" data-testid="compliance-flag">
              <span className="inline-block bg-red-100 text-red-700 px-2 py-0.5 rounded text-sm font-medium" data-testid="compliance-warning-text">"{f.phrase}"</span>
              <p className="text-sm text-slate-600 mt-2"><span className="font-medium text-slate-800">Why:</span> {f.issue}</p>
              {f.suggestion && <p className="text-sm text-green-700 mt-1"><span className="font-medium">Try:</span> {f.suggestion}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
